import { useEffect, useState } from "react";
import { api, type Signal } from "../lib/api";
import { duration, money, odds, outcome, signClass, signedPct } from "../lib/format";

/**
 * Řádek signálu je hlavní prvek celého rozhraní. Dvojice cen pod sebou ukazuje
 * to jediné, na čem záleží: o kolik je nabídka lepší než férová cena.
 */
function SignalRow({ signal, now, onOpen }: { signal: Signal; now: number; onOpen: (id: string) => void }) {
  const fairOdds = 1 / Number(signal.fair_prob);
  const edge = Number(signal.edge);
  const validMs = new Date(signal.valid_until).getTime() - now;
  const ttl = new Date(signal.valid_until).getTime() - new Date(signal.detected_at).getTime();
  const remaining = Math.max(0, Math.min(1, validMs / ttl));

  return (
    <article className="signal">
      <div className="signal-match">
        {signal.home_team} vs {signal.away_team}
      </div>
      <div className="signal-kickoff">
        výkop za {duration(new Date(signal.starts_at).getTime() - now)}
      </div>
      <div className="signal-meta">
        {signal.source_name}, {outcome(signal.outcome_code)}
        {signal.mode === "paper" && <> <span className="tag">papírový</span></>}
      </div>

      <div className="prices">
        <div className="price-value num">{odds(signal.offered_odds)}</div>
        <div className="price-label">nabídka</div>
        <div className={`edge num ${signClass(edge)}`}>{signedPct(edge)}</div>
        <div className="price-value secondary num">{odds(fairOdds)}</div>
        <div className="price-label">fér</div>
      </div>

      <div className="signal-actions">
        {signal.mode === "actionable" && <span>sázka {money(signal.suggested_stake)}</span>}
        <span>stáří {duration(now - new Date(signal.detected_at).getTime())}</span>
        {signal.status === "open" && validMs > 0 && (
          <span className="validity" title={`platí ještě ${duration(validMs)}`}>
            <i style={{ transform: `scaleX(${remaining})`, transition: "transform 1s linear" }} />
          </span>
        )}
        {signal.status !== "open" && <span className="tag">{statusLabel(signal.status)}</span>}
        <button className="action quiet" onClick={() => onOpen(signal.id)}>
          {signal.bet_id ? "Upravit záznam" : "Zaznamenat sázku"}
        </button>
      </div>
    </article>
  );
}

function statusLabel(status: string): string {
  return { open: "otevřený", expired: "vypršel", bet_placed: "vsazeno", skipped: "nesázeno" }[status] ?? status;
}

export function Signals({ onOpen }: { onOpen: (id: string) => void }) {
  const [signals, setSignals] = useState<Signal[] | null>(null);
  const [mode, setMode] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      api
        .signals(mode ? { mode } : {})
        .then((r) => !cancelled && setSignals(r.signals))
        .catch((e: Error) => !cancelled && setError(e.message));
    void load();
    const timer = setInterval(load, 20_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [mode]);

  if (error) return <p className="notice warn">Signály se nepodařilo načíst: {error}</p>;
  if (!signals) return <p className="empty">Načítám.</p>;

  const open = signals.filter((s) => s.status === "open");

  return (
    <>
      <h1>{open.length === 0 ? "Žádný otevřený signál" : `${open.length} otevřených signálů`}</h1>
      <p className="lede">
        Papírové signály vznikají u knih, kde se sázet nedá. Neposílá se u nich notifikace, ale
        měří se jim CLV, takže se z nich pozná, jestli systém předbíhá trh.
      </p>

      <div className="field-row" style={{ marginBottom: "1rem" }}>
        <div className="field" style={{ maxWidth: "12rem" }}>
          <label htmlFor="mode">Režim</label>
          <select id="mode" value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="">vše</option>
            <option value="actionable">jen akční</option>
            <option value="paper">jen papírové</option>
          </select>
        </div>
      </div>

      {signals.length === 0 ? (
        <p className="empty">
          Zatím nevznikl žádný signál. Buď ještě neproběhl sběr, nebo trh nenabízí nic nad prahem.
          Na ostrých ligách je to očekávaný stav, ne chyba.
        </p>
      ) : (
        <div className="board">
          {signals.map((signal) => (
            <SignalRow key={signal.id} signal={signal} now={now} onOpen={onOpen} />
          ))}
        </div>
      )}
    </>
  );
}
